import { useEffect, useMemo, useState } from 'react';

function Testimonials({ limit = 20, pageSize = null }) {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(`/api/testimonials?limit=${limit}&approved=true`);

        if (!response.ok) {
          throw new Error('Unable to load testimonials.');
        }

        const data = await response.json();
        setTestimonials(Array.isArray(data) ? data : []);
      } catch {
        setTestimonials([]);
        setError('Testimonials are currently unavailable. Please check back shortly.');
      } finally {
        setLoading(false);
      }
    };

    loadTestimonials();
  }, [limit]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, testimonials.length]);

  const totalPages = useMemo(() => {
    if (!pageSize || pageSize < 1) {
      return 1;
    }

    return Math.max(1, Math.ceil(testimonials.length / pageSize));
  }, [pageSize, testimonials.length]);

  const visibleTestimonials = useMemo(() => {
    if (!pageSize || pageSize < 1) {
      return testimonials;
    }

    const startIndex = (currentPage - 1) * pageSize;
    return testimonials.slice(startIndex, startIndex + pageSize);
  }, [currentPage, pageSize, testimonials]);

  const handlePreviousPage = () => {
    setCurrentPage((page) => Math.max(1, page - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((page) => Math.min(totalPages, page + 1));
  };

  return (
    <section className="content-page-block testimonials-section" aria-label="Customer testimonials">
      <h3 className="testimonials-title">Customer Testimonials</h3>
      <p className="testimonials-subheader">We pride ourselves on our high standard of customer service.</p>
      <p className="testimonials-subheader">
        But don&apos;t just take our word for it, read our customer reviews below.
      </p>

      {loading && <p className="status-text">Loading testimonials...</p>}
      {!loading && error && <p className="status-text">{error}</p>}

      {!loading && !error && testimonials.length === 0 && (
        <p className="status-text">No testimonials available yet.</p>
      )}

      {!loading && !error && testimonials.length > 0 && (
        <>
          <div className="testimonials-list">
            {visibleTestimonials.map((testimonial) => (
              <article className="testimonial-item" key={testimonial.id}>
                <strong>{testimonial.customerName || 'Verified Customer'}</strong>
                <p>{testimonial.text || ''}</p>
              </article>
            ))}
          </div>

          {pageSize && totalPages > 1 && (
            <div className="testimonials-pagination" aria-label="Testimonials pagination">
              <button
                type="button"
                className="testimonials-pagination-btn"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="testimonials-pagination-status">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="testimonials-pagination-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default Testimonials;
